import { API_ROOT } from '../../supplementary_components/requests';
import { addPopup } from '../../supplementary_components/store';
import { Form } from '../../general_components/forms/Form';
import { localDateToUSFormat } from '../../general_components/content_management/item_page/driver_page_components/payoutAdjustmentsSection';
import { dateStringToLocalDateString } from '../reports/weeklyReports';
import { dateFormatter } from '../../supplementary_components/supplementaryMethods';
import {
    DATAQ_OPTIONS,
    OOS_OPTIONS,
    RESULT_OPTIONS,
    US_STATES,
    calculatedSeverity,
    labelFor,
    syncFiles,
    todayIso,
    violationSuggestionLabel,
} from './safetyApi';
import { violationSummary } from './safetyEntriesField';

const dateColumn = (name, label, value, mandatory) => ({
    label,
    name,
    mandatory,
    autoComplete: 'off',
    calendarPickType: 'singleDate',
    hasConstraints: true,
    defaultValue: () => (value ? localDateToUSFormat(value) : ''),
    checks: [
        (entered) => {
            if (!mandatory && (entered === '' || entered == null)) return null;
            return dateFormatter(entered).date === null ? 'Invalid Input' : null;
        },
    ],
    onblur: (entered) => dateFormatter(entered).date || entered,
    processBeforeSending: (entered) => (entered ? dateStringToLocalDateString(entered) : null),
});

function assignPartyField(fieldName, equipment, label) {
    if (!equipment?.id || !label) return;
    const field = document.querySelector(`#inspectionForm [name="${fieldName}"]`);
    if (!field) return;
    field.value = label;
    field.setAttribute('data-value', JSON.stringify(equipment));
    field.classList.remove('error');
    const error = document.getElementById(`${fieldName}Error`);
    if (error) error.style.display = 'none';
    field.dispatchEvent(new Event('input', { bubbles: true }));
}

function partyValue(id, label, extra) {
    if (id == null || label == null || label === '' || label === '-') return null;
    return JSON.stringify({ id, ...extra });
}

function shortCode(item) {
    return violationSuggestionLabel(item);
}

function violationForm(entry, onSave, entriesSource) {
    return {
        formTitle: entry ? 'Edit Violation' : 'Add Violation',
        name: 'violationForm',
        customSubmitButton: 'Save',
        ignoreChangesTracker: true,
        columns: [
            {
                label: 'Code',
                name: 'code',
                mandatory: true,
                suggestionsEnabled: true,
                suggestionsLink: `${API_ROOT}sms-violations?pattern=`,
                dataValueRequired: true,
                autoComplete: 'off',
                suggestOnEachKey: true,
                afterMinimumChars: 1,
                displayValueGenerator: shortCode,
                defaultValue: () => (entry ? shortCode({
                    code: entry.code,
                    description: entry.officialDescription,
                    violationGroup: entry.violationGroup,
                }) : ''),
                predefinedDatValue: entry?.code ? JSON.stringify({
                    code: entry.code,
                    description: entry.officialDescription,
                    basic: entry.basic,
                    violationGroup: entry.violationGroup,
                    severityWeight: entry.baseWeight,
                }) : null,
            },
            {
                label: 'Description',
                name: 'description',
                type: 'textArea',
                rowCount: 3,
                defaultValue: () => entry?.description || '',
            },
            {
                label: 'Out of Service',
                name: 'outOfService',
                type: 'select',
                dataset: OOS_OPTIONS,
                defaultValue: () => entry?.outOfService || 'NONE',
            },
        ],
        buildRequestBody: (data) => {
            const code = data.code;
            if (!code?.code) {
                return { dismissWithCustomMsg: true, customMsg: 'Select a violation code' };
            }
            const existing = (typeof entriesSource === 'function' ? entriesSource() : entriesSource) || [];
            const duplicate = existing.some((item) => item !== entry && item.code?.toLowerCase() === code.code.toLowerCase());
            if (duplicate) {
                return { dismissWithCustomMsg: true, customMsg: `Violation code ${code.code} is already on this inspection` };
            }
            onSave({
                code: code.code,
                officialDescription: code.description,
                basic: code.basic,
                violationGroup: code.violationGroup,
                baseWeight: code.severityWeight,
                description: data.description || '',
                outOfService: data.outOfService || 'NONE',
            });
            return { dismissRequestWithSuccessMsg: true };
        },
    };
}

function citationForm(entry, onSave, { standalone, saveToApi }) {
    const columns = [
        {
            label: 'Ticket Number',
            name: 'ticketNumber',
            autoComplete: 'off',
            defaultValue: () => entry?.ticketNumber || '',
        },
    ];
    if (standalone) {
        columns.push(dateColumn('issuedDate', 'Date', entry?.date || entry?.issuedDate || todayIso(), true));
        columns.push({
            label: 'Driver',
            name: 'driver',
            mandatory: true,
            suggestionsEnabled: true,
            suggestionsLink: `${API_ROOT}drivers/namecontaines?statusagnostic=true&name=`,
            dataValueRequired: true,
            autoComplete: 'off',
            afterMinimumChars: 2,
            displayValueGenerator: (driver) => [driver.firstName, driver.lastName].filter(Boolean).join(' '),
            defaultValue: () => entry?.driverName && entry.driverName !== '-' ? entry.driverName : '',
            predefinedDatValue: partyValue(entry?.driverId, entry?.driverName, {
                firstName: entry?.driverName,
            }),
        });
        columns.push({
            label: 'Truck',
            name: 'truck',
            suggestionsEnabled: true,
            suggestionsLink: `${API_ROOT}trucks/allTrucksBySimilarTruckNumber?pattern=`,
            autoComplete: 'off',
            displayValueGenerator: (truck) => truck.truckNumber,
            defaultValue: () => entry?.truckNumber && entry.truckNumber !== '-' ? entry.truckNumber : '',
            predefinedDatValue: partyValue(entry?.truckId, entry?.truckNumber, { truckNumber: entry?.truckNumber }),
            customProcessChanges: (field, data) => {
                const raw = field.getAttribute('data-value');
                data.truck = raw ? JSON.parse(raw) : null;
                return data;
            },
        });
        columns.push({
            label: 'Trailer',
            name: 'trailer',
            suggestionsEnabled: true,
            suggestionsLink: `${API_ROOT}trailers/trailersBySimilarTrailerNumberForCurrentCompanyAndThirdPartyTrailers?crossCompany=false&pattern=`,
            autoComplete: 'off',
            displayValueGenerator: (trailer) => trailer.trailerNumber,
            defaultValue: () => entry?.trailerNumber && entry.trailerNumber !== '-' ? entry.trailerNumber : '',
            predefinedDatValue: partyValue(entry?.trailerId, entry?.trailerNumber, { trailerNumber: entry?.trailerNumber }),
            customProcessChanges: (field, data) => {
                const raw = field.getAttribute('data-value');
                data.trailer = raw ? JSON.parse(raw) : null;
                return data;
            },
        });
        columns.push({
            label: 'State',
            name: 'state',
            type: 'select',
            mandatory: true,
            dataset: ['', ...US_STATES],
            defaultValue: () => entry?.state || '',
        });
    }
    columns.push(
        {
            label: 'DataQ',
            name: 'dataQStatus',
            type: 'select',
            dataset: DATAQ_OPTIONS,
            defaultValue: () => entry?.dataQStatus || 'NOT_STARTED',
        },
        {
            label: 'Reason',
            name: 'reason',
            type: 'textArea',
            rowCount: 3,
            mandatory: true,
            defaultValue: () => entry?.reason || '',
        },
        {
            label: 'Lawyer',
            name: 'lawyerName',
            autoComplete: 'off',
            suggestionsEnabled: true,
            suggestionsLink: `${API_ROOT}lawyers?pattern=`,
            displayValueGenerator: (lawyer) => lawyer?.name ?? '',
            suggestOnEachKey: true,
            noValidation: true,
            defaultValue: () => entry?.lawyerName || '',
            processBeforeSending: (value) => (value ? String(value).trim() : null),
        },
        dateColumn('trialDate', 'Trial Date', entry?.trialDate, false),
        {
            label: 'Result',
            name: 'result',
            type: 'select',
            dataset: RESULT_OPTIONS,
            defaultValue: () => entry?.result || 'PENDING',
        },
        {
            label: 'Notes',
            name: 'notes',
            type: 'textArea',
            rowCount: 3,
            defaultValue: () => entry?.notes || '',
        },
        {
            label: 'Files',
            name: 'files',
            type: 'dropBox',
            fileType: 'any',
            multiple: true,
            file: null,
            files: [
                ...(entry?.existingFiles || []).map((file, index) => ({ ...file, name: file.fileName, lastModified: index + 1 })),
                ...(entry?.newFiles || []),
            ],
            ignoreAsRegularField: true,
        },
    );
    return {
        formTitle: entry?.id || entry ? 'Edit Citation' : 'Add Citation',
        name: saveToApi ? 'citationForm' : 'citationDraftForm',
        addLink: saveToApi ? `${API_ROOT}${entry?.id ? `citations/${entry.id}` : 'citations'}` : undefined,
        successMessage: saveToApi ? (entry?.id ? 'Citation was saved' : 'Citation was added') : undefined,
        customSubmitButton: 'Save',
        ignoreChangesTracker: !saveToApi,
        columns,
        buildRequestBody: (data, file, files) => {
            const selected = files || [];
            const draft = {
                id: entry?.id ?? null,
                ticketNumber: data.ticketNumber,
                issuedDate: data.issuedDate || null,
                driverId: data.driver?.id ?? null,
                driverName: data.driver ? [data.driver.firstName, data.driver.lastName].filter(Boolean).join(' ') : entry?.driverName,
                truckId: data.truck?.id ?? null,
                truckNumber: data.truck?.truckNumber || '',
                trailerId: data.trailer?.id ?? null,
                trailerNumber: data.trailer?.trailerNumber || '',
                state: data.state || null,
                dataQStatus: data.dataQStatus,
                reason: data.reason,
                lawyerName: data.lawyerName,
                trialDate: data.trialDate || null,
                result: data.result,
                notes: data.notes,
                existingFiles: selected.filter((item) => item.filePath),
                newFiles: selected.filter((item) => item instanceof File),
                removedPaths: (entry?.existingFiles || [])
                    .filter((item) => !selected.some((selectedFile) => selectedFile.filePath === item.filePath))
                    .map((item) => item.filePath),
            };
            if (!saveToApi) {
                onSave(draft);
                return { dismissRequestWithSuccessMsg: true };
            }
            return {
                ticketNumber: draft.ticketNumber,
                issuedDate: draft.issuedDate,
                driverId: draft.driverId,
                truckId: draft.truckId,
                trailerId: draft.trailerId,
                state: draft.state,
                dataQStatus: draft.dataQStatus,
                reason: draft.reason,
                lawyerName: draft.lawyerName,
                trialDate: draft.trialDate,
                result: draft.result,
                notes: draft.notes,
            };
        },
        _fileDraft: entry,
    };
}

export function openCitationForm({ record, onSaved }) {
    const conf = citationForm(record, null, { standalone: !record?.inspectionId, saveToApi: true });
    const originalBuild = conf.buildRequestBody;
    conf.buildRequestBody = (data, file, files) => {
        conf._selectedFiles = files || [];
        conf._originalFiles = record?.files || [];
        return originalBuild(data, file, files);
    };
    addPopup({
        content: Form,
        EZCloseAllowed: false,
        popupData: { configuration: conf },
        postSuccessAction: (saved) => {
            const selected = conf._selectedFiles || [];
            const removed = (conf._originalFiles || [])
                .filter((item) => !selected.some((file) => file.filePath === item.filePath))
                .map((item) => item.filePath);
            const added = selected.filter((item) => item instanceof File);
            syncFiles('citation', saved.id, conf._originalFiles, removed, added).finally(onSaved);
        },
    });
}

export function openInspectionForm(record, onSaved) {
    const violations = (record?.violations || []).map((violation) => ({ ...violation }));
    const citations = (record?.citations || []).map((citation) => ({
        ...citation,
        existingFiles: citation.files || [],
        newFiles: [],
        removedPaths: [],
    }));
    const conf = {
        formTitle: record?.id ? 'Edit Inspection' : 'Add Inspection',
        name: 'inspectionForm',
        addLink: `${API_ROOT}${record?.id ? `inspections/${record.id}` : 'inspections'}`,
        successMessage: record?.id ? 'Inspection was saved' : 'Inspection was added',
        customSubmitButton: 'Save',
        columns: [
            {
                label: 'Inspection Number',
                name: 'inspectionNumber',
                autoComplete: 'off',
                defaultValue: () => record?.inspectionNumber || '',
            },
            dateColumn('inspectionDate', 'Inspection Date', record?.inspectionDate || todayIso(), true),
            {
                label: 'Driver',
                name: 'driver',
                mandatory: true,
                suggestionsEnabled: true,
                suggestionsLink: `${API_ROOT}drivers/inspection-suggestions?name=`,
                dataValueRequired: true,
                autoComplete: 'off',
                afterMinimumChars: 2,
                displayValueGenerator: (driver) => [driver.firstName, driver.lastName].filter(Boolean).join(' '),
                defaultValue: () => record?.driverName || '',
                predefinedDatValue: partyValue(record?.driverId, record?.driverName, { firstName: record?.driverName }),
                afterSelection: (raw) => {
                    const driver = JSON.parse(raw);
                    assignPartyField('truck', driver.truck, driver.truck?.truckNumber);
                    assignPartyField('trailer', driver.trailer, driver.trailer?.trailerNumber);
                    return true;
                },
            },
            {
                label: 'Truck',
                name: 'truck',
                mandatory: true,
                suggestionsEnabled: true,
                suggestionsLink: `${API_ROOT}trucks/allTrucksBySimilarTruckNumber?pattern=`,
                dataValueRequired: true,
                autoComplete: 'off',
                displayValueGenerator: (truck) => truck.truckNumber,
                defaultValue: () => record?.truckNumber || '',
                predefinedDatValue: partyValue(record?.truckId, record?.truckNumber, { truckNumber: record?.truckNumber }),
            },
            {
                label: 'Trailer',
                name: 'trailer',
                suggestionsEnabled: true,
                suggestionsLink: `${API_ROOT}trailers/trailersBySimilarTrailerNumberForCurrentCompanyAndThirdPartyTrailers?crossCompany=false&pattern=`,
                autoComplete: 'off',
                displayValueGenerator: (trailer) => trailer.trailerNumber,
                defaultValue: () => record?.trailerNumber && record.trailerNumber !== '-' ? record.trailerNumber : '',
                predefinedDatValue: partyValue(record?.trailerId, record?.trailerNumber, { trailerNumber: record?.trailerNumber }),
                customProcessChanges: (field, data) => {
                    const raw = field.getAttribute('data-value');
                    data.trailer = raw ? JSON.parse(raw) : null;
                    return data;
                },
            },
            {
                label: 'Inspection Level',
                name: 'level',
                type: 'select',
                mandatory: true,
                dataset: [
                    { value: '', label: '' },
                    { value: '1', label: 'Level 1' },
                    { value: '2', label: 'Level 2' },
                    { value: '3', label: 'Level 3' },
                ],
                defaultValue: () => (record?.id ? String(record.level) : '2'),
            },
            {
                label: 'State',
                name: 'state',
                type: 'select',
                mandatory: true,
                dataset: ['', ...US_STATES],
                defaultValue: () => record?.state || '',
            },
            {
                type: 'safetyEntries',
                name: 'violations',
                label: 'Violations',
                addLabel: 'Add Violation',
                entries: violations,
                summary: violationSummary,
                buildForm: (entry, onSave) => violationForm(
                    entry,
                    onSave,
                    () => conf.columns.find((column) => column.name === 'violations').entries,
                ),
                onEntriesChange: (next, previous) => {
                    const input = document.querySelector('#inspectionForm [name="severityWeight"]');
                    if (!input) return;
                    const previousTotal = String(calculatedSeverity(previous));
                    if (input.value === previousTotal || input.value === '') {
                        input.value = String(calculatedSeverity(next));
                    }
                },
                processChanges: (column, data) => {
                    data.violations = column.entries || [];
                    return data;
                },
            },
            {
                label: 'Severity Weight',
                name: 'severityWeight',
                autoComplete: 'off',
                defaultValue: () => String(record?.severityWeight ?? 0),
            },
            {
                type: 'safetyEntries',
                name: 'citations',
                label: 'Citations',
                addLabel: 'Add Citation',
                entries: citations,
                summary: (entry) => entry.ticketNumber || 'No ticket number',
                buildForm: (entry, onSave) => citationForm(entry, onSave, { standalone: false, saveToApi: false }),
                processChanges: (column, data) => {
                    data.citations = column.entries || [];
                    return data;
                },
            },
            {
                label: 'Notes',
                name: 'notes',
                type: 'textArea',
                rowCount: 4,
                defaultValue: () => record?.notes || '',
            },
            {
                label: 'Files',
                name: 'files',
                type: 'dropBox',
                fileType: 'any',
                multiple: true,
                file: null,
                files: (record?.files || []).map((file, index) => ({ ...file, name: file.fileName, lastModified: index + 1 })),
                ignoreAsRegularField: true,
            },
        ],
        buildRequestBody: (data, file, files) => {
            const violationEntries = conf.columns.find((column) => column.name === 'violations').entries || [];
            const citationEntries = conf.columns.find((column) => column.name === 'citations').entries || [];
            const selectedFiles = files || [];
            conf._originalFiles = record?.files || [];
            conf._selectedFiles = selectedFiles;
            conf._citationEntries = citationEntries;
            const calculated = calculatedSeverity(violationEntries);
            const entered = Number(data.severityWeight);
            return {
                inspectionNumber: data.inspectionNumber,
                inspectionDate: data.inspectionDate,
                driverId: data.driver?.id,
                truckId: data.truck?.id,
                trailerId: data.trailer?.id ?? null,
                level: Number(data.level),
                state: data.state,
                notes: data.notes,
                violations: violationEntries.map((violation) => ({
                    code: violation.code,
                    description: violation.description,
                    outOfService: violation.outOfService || 'NONE',
                })),
                severityWeight: Number.isNaN(entered) ? calculated : entered,
                severityWeightOverridden: !Number.isNaN(entered) && entered !== calculated,
                citations: citationEntries.map((citation) => ({
                    id: citation.id,
                    ticketNumber: citation.ticketNumber,
                    dataQStatus: citation.dataQStatus,
                    reason: citation.reason,
                    lawyerName: citation.lawyerName,
                    trialDate: citation.trialDate || null,
                    result: citation.result,
                    notes: citation.notes,
                })),
            };
        },
    };
    addPopup({
        content: Form,
        EZCloseAllowed: false,
        popupData: { configuration: conf },
        postSuccessAction: (saved) => {
            const selected = conf._selectedFiles || [];
            const removed = (conf._originalFiles || [])
                .filter((item) => !selected.some((file) => file.filePath === item.filePath))
                .map((item) => item.filePath);
            const added = selected.filter((item) => item instanceof File);
            const previousIds = new Set((conf._citationEntries || []).filter((citation) => citation.id).map((citation) => citation.id));
            const created = (saved.citations || []).filter((citation) => !previousIds.has(citation.id));
            let createdIndex = 0;
            const uploads = [
                syncFiles('inspection', saved.id, conf._originalFiles, removed, added),
            ];
            (conf._citationEntries || []).forEach((citation) => {
                const match = citation.id
                    ? (saved.citations || []).find((item) => item.id === citation.id)
                    : created[createdIndex++];
                if (!match) return;
                if ((citation.removedPaths || []).length || (citation.newFiles || []).length) {
                    uploads.push(syncFiles('citation', match.id, citation.existingFiles, citation.removedPaths, citation.newFiles));
                }
            });
            Promise.all(uploads).finally(onSaved);
        },
    });
}

export function resultLabel(value) {
    return labelFor(RESULT_OPTIONS, value);
}
